import { MarkerType } from 'reactflow';
import { COLOR_VAR } from '@ricoh/ria.client.core/constants/ui/color';
import { newUUID } from '@ricoh/ria.client.core/utils/data/key';

import type { ProcessFlowEdge, ProcessFlowEdgeDefault, ProcessFlowEdgeLabel, ProcessFlowLayout } from '~/common/model/api/batch-type/process-flow';

import type { CreateEdgeOptions } from './_edge.type';

const edgeBase: Partial<ProcessFlowEdge> = {
  deletable: false,
  markerEnd: {
    type: MarkerType.Arrow,
    color: COLOR_VAR.SECONDARY_COLOR,
    strokeWidth: 1.25,
  },
  style: {
    strokeWidth: 1.25,
    stroke: COLOR_VAR.SECONDARY_COLOR,
  },
};

/** Create edge. */
export const createEdge = {
  default: ({ id, ...options }: CreateEdgeOptions<Pick<ProcessFlowEdgeDefault, 'data'>>): ProcessFlowEdgeDefault => ({
    ...edgeBase,
    ...options,
    id: id ?? newUUID(),
    type: 'default',
  }),

  label: ({ id, ...options }: CreateEdgeOptions<Pick<ProcessFlowEdgeLabel, 'data'>>): ProcessFlowEdgeLabel => ({
    ...edgeBase,
    ...options,
    id: id ?? newUUID(),
    type: 'label',
  }),
};

/**
 * Get connected edges.
 *
 * @param {ProcessFlowLayout} layout Layout.
 * @param {string} source Source node id.
 * @param {string} target Target node id.
 *
 * @returns {Array} Connected edges.
 */
export function getConnectedEdges(layout: ProcessFlowLayout, source: string, target: string): ProcessFlowEdge[] {
  return layout.edges.filter((edge) => edge.source === source && edge.target === target);
}

/**
 * Get inbound edges of node.
 *
 * @param {ProcessFlowLayout} data Data.
 * @param {string} id Node id.
 *
 * @returns {Array} Inbound edges.
 */
export function getInboundEdges(data: ProcessFlowLayout, id: string): ProcessFlowEdge[] {
  return data.edges.filter((edge) => edge.target === id);
}

/**
 * Get outbound edges of node.
 *
 * @param {ProcessFlowLayout} data Data.
 * @param {string} id Node id.
 *
 * @returns {Array} Outbound edges.
 */
export function getOutboundEdges(data: ProcessFlowLayout, id: string): ProcessFlowEdge[] {
  return data.edges.filter((edge) => edge.source === id);
}
